import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { RequestUnavailable } from "@/components/RequestUnavailable";
import { setRedirectIntent } from "@/lib/redirect-intent";
import { useAuth } from "@/lib/auth";

const SITE = "https://turkey-drive.com";

export type PublicRequest = {
  id: string;
  title: string;
  category: string;
  city: string | null;
  description: string | null;
  format: string | null;
  request_type: string | null;
  created_at: string | null;
  author_user_id: string | null;
  author_name: string | null;
  author_photo_url: string | null;
  responses_count: number;
};

async function loadPublicRequest(requestId: string): Promise<PublicRequest | null> {
  const { data, error } = await supabase.rpc("public_request", { request_id: requestId });
  if (error) return null;
  const rows = (data ?? []) as PublicRequest[];
  return rows[0] ?? null;
}

function pluralAnswers(n: number): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return "ответ";
  if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "ответа";
  return "ответов";
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
}

export const Route = createFileRoute("/req/$requestId")({
  loader: ({ params }) => loadPublicRequest(params.requestId),
  head: ({ params, loaderData }) => {
    const url = `${SITE}/req/${params.requestId}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Вопрос — TurkeyDrive" }, { name: "robots", content: "noindex" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const title = `${loaderData.title} — Спросить своих в TurkeyDrive`;
    const description =
      (loaderData.description || "").slice(0, 155) ||
      `${loaderData.category} · ${loaderData.city ?? "Вся Турция"} · вопрос в сообществе TurkeyDrive.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PublicRequestPage,
  errorComponent: () => <RequestUnavailable />,
  notFoundComponent: () => <RequestUnavailable />,
});

function PublicRequestPage() {
  const request = Route.useLoaderData();
  const { requestId } = Route.useParams();
  const { user } = useAuth();

  if (!request) return <RequestUnavailable />;

  function handleCta() {
    const target = `/requests?request=${requestId}`;
    if (user) {
      window.location.assign(target);
      return;
    }
    setRedirectIntent(target);
    window.location.assign(`/auth?redirect=${encodeURIComponent(target)}`);
  }

  return (
    <main className="min-h-screen w-full min-w-0 max-w-full overflow-x-clip bg-gradient-to-b from-td-teal/10 via-white to-white pb-28">
      <div className="mx-auto w-full max-w-[520px] px-4 pt-5">
        <a href="/" className="text-[13px] text-td-muted hover:text-td-dark">
          ← TurkeyDrive
        </a>

        <div className="mt-4 flex flex-wrap items-center gap-1.5">
          {request.request_type && (
            <span className="rounded-full bg-td-teal/10 px-2.5 py-0.5 text-xs font-semibold text-td-teal">
              {request.request_type}
            </span>
          )}
          <span className="max-w-full rounded-full bg-td-soft px-2.5 py-0.5 text-xs text-td-dark/80 [overflow-wrap:anywhere]">
            {request.category}
          </span>
          <span className="max-w-full rounded-full bg-td-soft px-2.5 py-0.5 text-xs text-td-dark/80 [overflow-wrap:anywhere]">
            {request.city || "Вся Турция"}
          </span>
          {request.format && (
            <span className="max-w-full rounded-full bg-td-soft px-2.5 py-0.5 text-xs text-td-dark/80 [overflow-wrap:anywhere]">
              {request.format}
            </span>
          )}
        </div>

        <h1 className="mt-4 font-brand text-2xl font-bold leading-tight text-td-dark [overflow-wrap:anywhere]">
          {request.title}
        </h1>

        {request.description && request.description !== request.title && (
          <p className="mt-4 whitespace-pre-line [overflow-wrap:anywhere] text-[15px] leading-relaxed text-td-dark">
            {request.description}
          </p>
        )}

        {request.author_name && (
          <div className="mt-6 flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3">
            {request.author_photo_url ? (
              <img decoding="async"
                src={request.author_photo_url}
                alt={request.author_name}
                className="h-11 w-11 shrink-0 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-td-soft text-td-muted">
                🙂
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs text-td-muted">Спрашивает</div>
              <div className="text-sm font-semibold text-td-dark [overflow-wrap:anywhere]">{request.author_name}</div>
            </div>
            {request.created_at && (
              <div className="ml-auto shrink-0 text-xs text-td-muted">{formatDate(request.created_at)}</div>
            )}
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-td-line bg-td-soft/40 p-4 text-center">
          <p className="text-sm font-semibold text-td-dark">
            {request.responses_count} {pluralAnswers(request.responses_count)}
          </p>
          <p className="mt-1 text-[13px] text-td-muted">Ответы видят только резиденты сообщества.</p>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-td-line bg-white/95 p-4 pb-[calc(env(safe-area-inset-bottom)+16px)] backdrop-blur">
        <div className="mx-auto max-w-[520px]">
          <button
            type="button"
            onClick={handleCta}
            className="cta-solid w-full rounded-full px-6 py-3.5 text-base"
          >
            {user ? "Открыть ветку" : "Войти, чтобы прочитать ответы"}
          </button>
        </div>
      </div>
    </main>
  );
}
